import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { ASSETS, buildWhatsAppUrl } from "@/constants/site";
import { Target, Eye, Heart, Award, Users, ShieldCheck, Microscope, Stethoscope, ArrowRight } from "lucide-react";

const pillars = [
  { icon: Target, title: "Missão", text: "Oferecer diagnóstico por imagem veterinário de excelência, com precisão técnica, ética e acolhimento ao tutor e ao paciente." },
  { icon: Eye,    title: "Visão",   text: "Ser referência em Minas Gerais em diagnóstico por imagem veterinária, apoiando a decisão clínica de médicos veterinários encaminhadores." },
  { icon: Heart,  title: "Valores", text: "Profissionalismo, empatia com os pacientes, atualização constante e compromisso com resultados confiáveis." },
];

const stats = [
  { icon: Award,       number: "15+",  label: "anos de experiência" },
  { icon: Microscope,  number: "7",    label: "modalidades de exame" },
  { icon: Users,       number: "3k+",  label: "pets diagnosticados" },
  { icon: ShieldCheck, number: "100%", label: "anestesia com veterinário" },
];

export default function About() {
  return (
    <>
      <SEO
        title="Quem Somos"
        description="Conheça a Visiovet — centro dedicado exclusivamente ao diagnóstico por imagem veterinária em Belo Horizonte. Nossa história, missão, equipamentos e equipe especializada."
        path="/quem-somos"
      />

      {/* HERO */}
      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Quem somos</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Um centro dedicado <span className="text-brand-500">exclusivamente</span> ao diagnóstico por imagem veterinário
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              A Visiovet Diagnóstico Veterinário é um centro de imagem médica dedicado 100% à medicina veterinária em Belo Horizonte. Foi criada com a proposta de reunir, em um só lugar, tecnologia de ponta e equipe altamente especializada para apoiar a decisão clínica do médico veterinário encaminhador.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Aqui, cada paciente é conduzido por um protocolo próprio — do agendamento à entrega do laudo — sempre com anestesia acompanhada por médico veterinário anestesista e biossegurança em cada etapa.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={buildWhatsAppUrl("Olá Visiovet! Vim conhecer a clínica pelo site.")}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-6 py-3 shadow-lg shadow-brand-500/30 hover:bg-brand-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
              >
                Falar no WhatsApp <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-brand-50 rounded-3xl -z-0" />
              <img src={ASSETS.facade} alt="Fachada Visiovet Barro Preto" className="relative z-10 rounded-3xl shadow-xl w-full aspect-[4/5] object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/10 text-brand-300 flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading text-3xl font-bold">{s.number}</div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION VALUES */}
      <section className="bg-slate-50 section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Nosso propósito</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">O que nos move todos os dias</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((c) => (
              <div key={c.title} className="bg-white rounded-2xl border border-slate-200 p-8 hover:border-brand-300 hover:shadow-lg transition-[border-color,box-shadow] duration-300">
                <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center mb-5">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900">{c.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM / APPROACH */}
      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <img src={ASSETS.vetTeam} alt="Equipe veterinária cuidando de cachorro" className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover" />
          <div>
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Como trabalhamos</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">Diagnóstico com precisão, cuidado com o paciente</h2>
            <ul className="space-y-4">
              {[
                { icon: Stethoscope, title: "Recepção com encaminhamento", text: "Recebemos apenas pacientes com encaminhamento do veterinário assistente, garantindo indicação clínica adequada." },
                { icon: ShieldCheck, title: "Anestesia veterinária dedicada",  text: "Cada exame que exige sedação conta com médico veterinário anestesista e monitoração multiparamétrica." },
                { icon: Microscope,  title: "Laudo por radiologista veterinário", text: "Imagens interpretadas por profissionais com formação específica em radiologia veterinária, em prazo curto." },
              ].map((it) => (
                <li key={it.title} className="flex gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                    <it.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{it.title}</h3>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{it.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* STRUCTURE GALLERY */}
      <section className="bg-slate-50 section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Estrutura</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-10">Ambientes preparados para cada exame</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { img: ASSETS.mri,        label: "Sala de Ressonância" },
              { img: ASSETS.tomograph,  label: "Sala de Tomografia" },
              { img: ASSETS.ultrasound, label: "Sala de Ultrassom" },
              { img: ASSETS.xray,       label: "Sala de Raio-X" },
            ].map((g) => (
              <div key={g.label} className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-slate-100">
                <img src={g.img} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-heading font-semibold">{g.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
